import { useEffect, useState, type ReactNode } from 'react'
import { config } from './config'
import { Loading } from './auth'

// ── Types (mirror /api/admin/validation) ──────────────────────────────────────

type Cell = { model: number; twin: number; floor: number }
type Dims = Record<string, Cell>
type ModelRow = { code: string; description: string | null; folds: number; cars: number; dims: Dims }
type Pooled = { folds: number; cars: number; dims: Dims }
type ValidationData = {
  batch_size: number
  computed_at: string | null
  pooled: Pooled | null
  models: ModelRow[]
}

const DIMS: { key: string; label: string; note?: string }[] = [
  { key: 'bundle', label: 'Package bundle' },
  { key: 'color', label: 'Color' },
  { key: 'intr', label: 'Interior' },
  { key: 'rim', label: 'Rim' },
  { key: 'joint', label: 'Full configuration', note: 'sampling limit is approximate here — see method' },
]

const MIN_FOLDS = 8   // below this, per-model numbers are noisy; hide the floor, lean on the twin
const pct = (n: number) => `${Math.round(n * 100)}%`
const OCEAN = '#0653B6'

// Best achievable = the higher of our two reference predictors on this dimension
// (on marginals the raw distribution beats our engine; on the joint the engine wins).
const bestOf = (c: Cell) => Math.max(c.twin, c.floor)

// ── One bar, shared by both charts so they read identically ────────────────────

function Bar({ label, fill, value, ticks, note }: {
  label: string
  fill: number
  value: ReactNode
  ticks?: { at: number; dashed?: boolean; title?: string }[]
  note?: string
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between">
        <span className="text-xs font-bold uppercase tracking-[0.05em] text-black">{label}</span>
        <span className="text-[11px] tabular-nums text-[#666]">{value}</span>
      </div>
      <div className="relative h-5 w-full overflow-hidden bg-[#F0F2F5]">
        <div className="h-full transition-[width] duration-500" style={{ width: pct(Math.min(fill, 1)), background: OCEAN }} />
        {ticks?.map((t, i) => (
          <div
            key={i}
            className={`absolute top-0 h-full ${t.dashed ? 'border-l border-dashed border-[#8A97AD]' : 'border-l-2 border-[#1A365D]'}`}
            style={{ left: pct(Math.min(t.at, 1)) }}
            title={t.title}
          />
        ))}
      </div>
      {note && <div className="text-[10px] italic text-[#999]">{note}</div>}
    </div>
  )
}

// ── Collapsible section (only the headline opens by default) ───────────────────

function Section({ title, subtitle, defaultOpen, children }: {
  title: string; subtitle?: string; defaultOpen?: boolean; children: ReactNode
}) {
  return (
    <details open={defaultOpen} className="group border border-[#E5E7EC]">
      <summary className="flex cursor-pointer items-center gap-2 bg-[#FAFBFC] px-4 py-2.5 text-xs font-bold uppercase tracking-[0.08em] text-[#444]">
        <span className="text-[9px] text-[#999] group-open:rotate-90">▸</span>{title}
      </summary>
      <div className="space-y-4 px-4 py-4">
        {subtitle && <p className="text-xs leading-relaxed text-[#666]">{subtitle}</p>}
        {children}
      </div>
    </details>
  )
}

function ModelCell({ cell, muted }: { cell: Cell | undefined; muted: boolean }) {
  if (!cell) return <td className="px-3 py-2 text-right text-[#CFD3DB]">—</td>
  const ratio = bestOf(cell) > 0 ? Math.min(cell.model / bestOf(cell), 1) : 1
  const tone = muted ? 'text-[#BBB]' : ratio >= 0.95 ? 'text-bmw-ocean' : 'text-black'
  return (
    <td className="px-3 py-2 text-right tabular-nums" title={`${pct(cell.model)} of ${pct(bestOf(cell))} achievable`}>
      <span className={`font-bold ${tone}`}>{pct(cell.model)}</span>
    </td>
  )
}

export function AdminDataBenchmark() {
  const [data, setData] = useState<ValidationData | null>(null)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    fetch(`${config.apiUrl}/api/admin/validation`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : Promise.reject()))
      .then(setData)
      .catch(() => setErr('Could not load validation results'))
  }, [])

  if (err) return <p className="text-sm text-black">⚠ {err}</p>
  if (!data) return <Loading />

  if (!data.pooled) {
    return (
      <div className="space-y-3">
        <h1 className="text-[15px] font-bold uppercase tracking-[0.08em] text-black">Data benchmark</h1>
        <p className="text-xs text-[#999]">
          No validation results yet. Run <code className="bg-[#F0F2F5] px-1">python validation.py --write</code> in the engine to populate them.
        </p>
      </div>
    )
  }

  const p = data.pooled
  const B = data.batch_size
  const computed = data.computed_at ? new Date(data.computed_at).toLocaleDateString() : '—'

  return (
    <div className="max-w-4xl space-y-4">
      <div>
        <h1 className="text-[15px] font-bold uppercase tracking-[0.08em] text-black">Data benchmark</h1>
        <p className="mt-1 text-xs text-[#999]">
          How closely the model predicts the configuration mix of real orders — walk-forward backtest against what
          was actually ordered. {p.cars.toLocaleString()} test cars · {p.folds} folds · computed {computed}.
        </p>
      </div>

      <Section
        title="How close to the best possible"
        defaultOpen
        subtitle="Share of the maximum accuracy achievable on this data (100% = as good as any predictor we've measured could do, even knowing current demand). 'Best achievable' is the better of our two references on each dimension."
      >
        <div className="space-y-2">
          {DIMS.map(d => {
            const c = p.dims[d.key]
            if (!c) return null
            const ratio = bestOf(c) > 0 ? Math.min(c.model / bestOf(c), 1) : 1
            return (
              <Bar key={d.key} label={d.label} fill={ratio}
                value={<span className="text-sm font-bold text-bmw-ocean">{pct(ratio)}</span>} />
            )
          })}
        </div>
      </Section>

      <Section
        title="Absolute mix accuracy"
        subtitle={`Raw share of a ${B}-car batch's mix the model places correctly (100% = predicted mix matches the ordered mix exactly). Ticks mark the best achievable (solid) and the theoretical sampling limit (dashed).`}
      >
        <div className="space-y-4">
          {DIMS.map(d => {
            const c = p.dims[d.key]
            if (!c) return null
            return (
              <Bar key={d.key} label={d.label} fill={c.model} note={d.note}
                ticks={[
                  { at: c.twin, title: `best achievable (informed twin): ${pct(c.twin)}` },
                  { at: c.floor, dashed: true, title: `sampling limit: ${pct(c.floor)}` },
                ]}
                value={<>
                  <span className="text-sm font-bold text-black">{pct(c.model)}</span>
                  <span className="mx-1 text-[#CFD3DB]">·</span>best {pct(c.twin)} · limit {pct(c.floor)}
                </>} />
            )
          })}
        </div>
        <div className="flex flex-wrap gap-x-5 gap-y-1 text-[10px] text-[#999]">
          <span className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-4" style={{ background: OCEAN }} /> current model</span>
          <span className="flex items-center gap-1.5"><span className="inline-block h-3 border-l-2 border-[#1A365D]" /> best achievable (our method)</span>
          <span className="flex items-center gap-1.5"><span className="inline-block h-3 border-l border-dashed border-[#8A97AD]" /> sampling limit</span>
        </div>
      </Section>

      <Section title="By model" subtitle={`Absolute mix accuracy per dimension (share of the batch placed correctly); hover a cell for the achievable ceiling. Models under ${MIN_FOLDS} folds ("low data") are greyed — too few folds for a reliable estimate.`}>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b-2 border-bmw-ocean text-[10px] font-bold uppercase tracking-[0.1em] text-bmw-ocean">
                <th className="px-3 py-2.5 text-left">Model</th>
                <th className="px-3 py-2.5 text-right">Folds</th>
                <th className="px-3 py-2.5 text-right">Cars</th>
                {DIMS.map(d => <th key={d.key} className="px-3 py-2.5 text-right">{d.label.split(' ')[0]}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.models.map(m => {
                const muted = m.folds < MIN_FOLDS
                return (
                  <tr key={m.code} className="border-b border-[#E5E7EC] last:border-b-0 hover:bg-[#FAFBFC]">
                    <td className="px-3 py-2 text-black">
                      <span className="font-bold">{m.description || m.code}</span>
                      <span className="ml-1.5 font-mono text-[10px] text-[#999]">{m.code}</span>
                      {muted && <span className="ml-2 border border-[#CFD3DB] px-1 text-[8px] font-bold uppercase text-[#999]">low data</span>}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-[#999]">{m.folds}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-[#666]">{m.cars.toLocaleString()}</td>
                    {DIMS.map(d => <ModelCell key={d.key} cell={m.dims[d.key]} muted={muted} />)}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="Method & assumptions">
        <div className="space-y-2 text-xs leading-relaxed text-[#444]">
          <p><b>Walk-forward backtest.</b> For each model, orders are ordered by date; we repeatedly train on the past and
            predict the config mix of the next {B}-car batch, scoring against what was actually ordered
            (accuracy = 1 − total-variation distance). Only mature folds (≥40% of history as training) count.</p>
          <p><b>Best achievable.</b> The higher of two references per dimension: the <i>informed twin</i> (our engine trained on the
            window around each batch, excluding it — same method, current data) and the <i>sampling floor</i> (the raw local demand
            distribution). The model can't beat the twin, so their gap is the cost of forecasting forward.</p>
          <p><b>Sampling limit — assumptions.</b> The floor is a near-model-free estimate, not an absolute bound, holding only under:
            (a) the surrounding window approximates current demand (drift biases it); (b) it is estimated from a few hundred cars,
            so the full-config limit is noisy; (c) on the sparse full-config space the total-variation-optimal predictor is slightly
            more concentrated than the raw distribution, so the model can exceed this floor there — it is a clean limit on the
            marginals only; (d) it captures sampling noise given current demand, not the extra error of forecasting forward, and is
            specific to batch size {B} and this metric.</p>
        </div>
      </Section>
    </div>
  )
}
